import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useDataClock } from '../data';
import { combine } from '../data/queries/combine';
import { useRemediations, useThreatEvents } from '../data/queries/hooks';
import {
  DEFAULT_ROI_PERIOD,
  ROI_PERIODS,
  ROI_PERIOD_WORDS,
  mttrEfficiency,
  roiCounts,
  roiWeeks,
  type RoiCounts,
  type RoiPeriod,
} from '../lib/roi';
import ErrorState from '../ui/ErrorState';
import Skeleton from '../ui/Skeleton';

interface ROIPageProps {
  onBack: () => void;
}

/** What a box says for one period, from the counts of that period. */
interface Figure {
  value: string;
  formula: string[];
  extra?: React.ReactNode;
}

/** Every figure of the page is counted from what happened in a period, which each box lets a person choose. */
type Counts = Record<RoiPeriod, RoiCounts | null>;

const PERIOD_SHORT: Record<RoiPeriod, string> = { '7d': '7d', '30d': '30d', '1y': '1y' };

/** A year is twelve 30-day values: its box says so under its formula. */
const YEAR_NOTE = '12 × the 30-day value';

/**
 * One figure of the page, in its own box, with its own period (client notes 10/26, ROI 1). It is defined here, not inside
 * the page, so choosing a period is not lost when the page draws again (typing a value would otherwise reset it).
 */
const MetricCard: React.FC<{
  title: string;
  counts: Counts;
  /** loading: the first read is under way; error: it failed; ready: the counts are the tenant's. */
  status: 'loading' | 'error' | 'ready';
  /** What the box says for a period, or why it cannot say it (the box then reads Unavailable, with that reason). */
  figure: (counts: RoiCounts, period: RoiPeriod) => Figure | string;
}> = ({ title, counts, status, figure }) => {
  const [period, setPeriod] = useState<RoiPeriod>(DEFAULT_ROI_PERIOD);
  const current = counts[period];
  const made = status === 'ready' && current ? figure(current, period) : null;
  const shown = typeof made === 'string' ? null : made;
  const reason = typeof made === 'string' ? made : null;

  return (
    <div className="bg-arbitium-dark/50 backdrop-blur-sm rounded-xl p-6 border border-arbitium-primary/20">
      <div className="flex items-start justify-between gap-2 mb-4">
        <h3 className="text-gray-400 font-medium">{title}</h3>
        <div role="group" aria-label={`Period of ${title}`} className="flex shrink-0 gap-1">
          {ROI_PERIODS.map((item) => (
            <button
              key={item}
              type="button"
              aria-pressed={period === item}
              aria-label={ROI_PERIOD_WORDS[item]}
              onClick={() => setPeriod(item)}
              className={`rounded-md border px-2 py-0.5 text-xs font-medium ${
                period === item
                  ? 'border-arbitium-primary/60 bg-arbitium-primary/20 text-white'
                  : 'border-arbitium-primary/20 text-gray-400 hover:text-white'
              }`}
            >
              {PERIOD_SHORT[item]}
            </button>
          ))}
        </div>
      </div>
      {status === 'loading' ? (
        <Skeleton lines={3} label="Loading the figures…" />
      ) : shown ? (
        <>
          <div className="text-3xl font-bold text-white mb-4">{shown.value}</div>
          <div className="border-t border-arbitium-primary/10 pt-4">
            <p className="text-xs text-gray-500 mb-1">Formula:</p>
            {shown.formula.map((line, i) => (
              <p key={i} className="text-sm text-gray-400 font-mono">{line}</p>
            ))}
            {period === '1y' && <p className="text-sm text-gray-400 font-mono">{YEAR_NOTE}</p>}
            {shown.extra}
          </div>
        </>
      ) : (
        <>
          <div className="text-3xl font-bold text-gray-500 mb-4">Unavailable</div>
          <p className="text-sm text-gray-500">
            {status === 'error'
              ? 'The figures could not be read.'
              : reason ?? `There is not enough data in the last ${ROI_PERIOD_WORDS[period]}.`}
          </p>
        </>
      )}
    </div>
  );
};

const ROIPage: React.FC<ROIPageProps> = ({ onBack }) => {
  const [analystRate, setAnalystRate] = useState(() => {
    const saved = localStorage.getItem('arbitium-roi-analystRate');
    return saved ? Number(saved) : 75;
  });
  const [manualTime, setManualTime] = useState(() => {
    const saved = localStorage.getItem('arbitium-roi-manualTime');
    return saved ? Number(saved) : 45;
  });
  const [annualSalary, setAnnualSalary] = useState(() => {
    const saved = localStorage.getItem('arbitium-roi-annualSalary');
    return saved ? Number(saved) : 95000;
  });

  // Persist settings to localStorage
  useEffect(() => {
    localStorage.setItem('arbitium-roi-analystRate', analystRate.toString());
  }, [analystRate]);

  useEffect(() => {
    localStorage.setItem('arbitium-roi-manualTime', manualTime.toString());
  }, [manualTime]);

  useEffect(() => {
    localStorage.setItem('arbitium-roi-annualSalary', annualSalary.toString());
  }, [annualSalary]);

  // The threats and the remediations come from the data source the console reads (Demo or Live), the same records the
  // feed and the Overview count, so the figures here agree with them.
  const { refreshNow } = useDataClock();
  const threatsRead = useThreatEvents();
  const remediationsRead = useRemediations();
  const read = combine([threatsRead, remediationsRead]);
  const status: 'loading' | 'error' | 'ready' = read.error ? 'error' : read.loading || !read.ready ? 'loading' : 'ready';
  const threatRows = threatsRead.data;
  const remediationRows = remediationsRead.data;
  const countedAt = read.updatedAt;
  const counts = useMemo<Counts>(() => {
    const inputs = { threatEvents: threatRows ?? [], remediations: remediationRows ?? [] };
    const nowMs = countedAt || Date.now();
    return {
      '7d': roiCounts(inputs, nowMs, '7d'),
      '30d': roiCounts(inputs, nowMs, '30d'),
      '1y': roiCounts(inputs, nowMs, '1y'),
    };
  }, [threatRows, remediationRows, countedAt]);

  const money = (value: number, digits = 0) =>
    `$${value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;

  // Time saved (in hours) by the remediations the console finished, which would have taken a person manualTime each.
  const timeSaved = (c: RoiCounts) => (c.remediated * manualTime) / 60;

  const minutes = (value: number) => `${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} min`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="p-2 text-gray-400 hover:text-white hover:bg-arbitium-primary/10 rounded-xl transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <span>💰</span> ROI Dashboard
          </h1>
          <p className="text-gray-400 mt-1">Real-time value metrics from Arbitium automation</p>
        </div>
      </div>

      {status === 'error' && (
        <ErrorState title="Could not load the figures" onRetry={() => void refreshNow()}>
          Nothing was changed. Try again in a moment.
        </ErrorState>
      )}

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-3 gap-6">
        <MetricCard
          title="💵 COST SAVINGS"
          counts={counts}
          status={status}
          figure={(c) => ({
            value: money(timeSaved(c) * analystRate),
            formula: [
              `${c.remediated} threats ×`,
              `${manualTime} min × $${analystRate}/hr`,
              `= ${money(timeSaved(c) * analystRate, 2)}`,
            ],
          })}
        />

        <MetricCard
          title="⏱️ TIME SAVINGS"
          counts={counts}
          status={status}
          figure={(c) => ({
            value: `${timeSaved(c).toFixed(1)} hours`,
            formula: [
              `${c.remediated} threats ×`,
              `${manualTime} min avg manual time`,
              `= ${c.remediated * manualTime} min = ${timeSaved(c).toFixed(1)} hrs`,
            ],
          })}
        />

        <MetricCard
          title="⚡ MTTR EFFICIENCY"
          counts={counts}
          status={status}
          figure={(c) => {
            // Pre Arbitium is the Mean Time to Remediate of the Values box; Post Arbitium is measured (client notes 10/26, ROI 3).
            const gain = mttrEfficiency(c, manualTime);
            if (!gain || c.meanMinutes == null) {
              return 'No remediation that finished in this period has both its times, so the time with Arbitium cannot be measured.';
            }
            return {
              value: `${gain.gainPct.toFixed(0)}% gain`,
              formula: [
                `Pre Arbitium: ${c.remediated} threats × ${manualTime} min`,
                `= ${minutes(gain.preMinutes)}`,
                `Post Arbitium: ${c.remediated} threats × ${c.meanMinutes.toFixed(1)} min`,
                `= ${minutes(gain.postMinutes)}`,
                `(${minutes(gain.preMinutes)} − ${minutes(gain.postMinutes)}) ÷ ${minutes(gain.preMinutes)}`,
                `= ${gain.gainPct.toFixed(0)}% less time to remediate`,
              ],
            };
          }}
        />

        <MetricCard
          title="📊 THREATS MANAGED"
          counts={counts}
          status={status}
          figure={(c) => ({
            // Threats Managed is the sum of its four severities.
            value: `${c.managed} total`,
            formula: [`Breakdown:`],
            extra: (
              <div className="mt-2 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-red-400">🔴 Critical:</span>
                  <span className="text-white">{c.bySeverity.critical}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-orange-400">🟠 High:</span>
                  <span className="text-white">{c.bySeverity.high}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-yellow-400">🟡 Medium:</span>
                  <span className="text-white">{c.bySeverity.medium}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-green-400">🟢 Low:</span>
                  <span className="text-white">{c.bySeverity.low}</span>
                </div>
              </div>
            ),
          })}
        />

        <MetricCard
          title="👤 ANALYST CAPACITY"
          counts={counts}
          status={status}
          figure={(c, period) => {
            // Capacity is a rate: the hours saved against the hours a full-time analyst works in the same stretch.
            const workingHours = 40 * roiWeeks(period);
            const fte = timeSaved(c) / workingHours;
            return {
              value: `+${fte.toFixed(2)} FTE`,
              formula: [
                `${timeSaved(c).toFixed(1)} hrs saved ÷`,
                `${workingHours.toFixed(0)} hrs (40 hrs/week × ${roiWeeks(period).toFixed(1)} weeks)`,
                `= ${fte.toFixed(2)} FTE equivalent`,
              ],
            };
          }}
        />

        {/* Values: what the figures above are worked out from, the sixth box of the grid */}
        <div className="bg-arbitium-dark/50 backdrop-blur-sm rounded-xl p-6 border border-arbitium-primary/20">
          <h3 className="text-gray-400 font-medium mb-4">⚙️ VALUES</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="roi-contractor-rate" className="text-sm text-gray-400 block mb-2">Contractor Hourly Rate</label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">$</span>
                <input
                  id="roi-contractor-rate"
                  type="number"
                  value={analystRate}
                  onChange={(e) => setAnalystRate(Number(e.target.value))}
                  className="bg-arbitium-darker border border-arbitium-primary/30 rounded-lg px-3 py-2 text-white w-24 focus:outline-none focus:border-arbitium-primary"
                />
                <span className="text-gray-500">/hr</span>
              </div>
            </div>
            <div>
              <label htmlFor="roi-fte-salary" className="text-sm text-gray-400 block mb-2">FTE Annual Salary</label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">$</span>
                <input
                  id="roi-fte-salary"
                  type="number"
                  value={annualSalary}
                  onChange={(e) => setAnnualSalary(Number(e.target.value))}
                  className="bg-arbitium-darker border border-arbitium-primary/30 rounded-lg px-3 py-2 text-white w-32 focus:outline-none focus:border-arbitium-primary"
                />
                <span className="text-gray-500">/yr</span>
              </div>
            </div>
            <div>
              <label htmlFor="roi-mean-time" className="text-sm text-gray-400 block mb-2">Mean Time to Remediate</label>
              <div className="flex items-center gap-2">
                <input
                  id="roi-mean-time"
                  type="number"
                  value={manualTime}
                  onChange={(e) => setManualTime(Number(e.target.value))}
                  className="bg-arbitium-darker border border-arbitium-primary/30 rounded-lg px-3 py-2 text-white w-24 focus:outline-none focus:border-arbitium-primary"
                />
                <span className="text-gray-500">minutes</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROIPage;
